import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import PayrollService from '../../services/payroll';
import UserService from '../../services/user';
import { formatARS, formatDateAR, isDateWithinRange, nowDateInput, parseDateInput, resolveCashierName } from './payroll.utils';

const TYPES = ['BONUS', 'DISCOUNT'];

const emptyForm = {
  _id: null,
  user_id: '',
  type: 'BONUS',
  amount: '',
  description: '',
  effective_date: nowDateInput(),
};

const validateForm = (form) => {
  const errors = {};
  if (!form.user_id) errors.user_id = 'El cajero es obligatorio';
  if (!form.type) errors.type = 'El tipo es obligatorio';
  if (!form.amount || Number(form.amount) <= 0) errors.amount = 'El monto debe ser mayor a 0';
  if (!form.effective_date) errors.effective_date = 'La fecha efectiva es obligatoria';
  return errors;
};

const buildPayload = (form) => ({
  user_id: form.user_id,
  type: form.type,
  amount: Number(form.amount),
  description: form.description?.trim() || '',
  effective_date: new Date(`${form.effective_date}T00:00:00`).toISOString(),
});

const asArray = (payload) => (Array.isArray(payload) ? payload : []);

function PayrollAdjustmentsPage({ storeId }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    from: '',
    to: '',
    user_id: '',
    type: '',
  });

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [feedback, setFeedback] = useState({ type: null, message: '' });

  const usersMap = useMemo(() => {
    const map = new Map();
    asArray(users).forEach((u) => map.set(u._id || u.id, u));
    return map;
  }, [users]);

  const filteredRows = useMemo(() => {
    const safeRows = asArray(rows);
    return safeRows
      .filter((item) => {
        if (filters.user_id && item.user_id !== filters.user_id) return false;
        if (filters.type && item.type !== filters.type) return false;
        if ((filters.from || filters.to) && !isDateWithinRange(item.effective_date, filters.from, filters.to)) return false;
        return true;
      })
      .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
  }, [rows, filters]);

  const loadData = async () => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const [adjustments, usersData] = await Promise.all([
        PayrollService.listAdjustments(storeId),
        UserService.getAll(),
      ]);
      setRows(asArray(adjustments));
      setUsers(asArray(usersData));
    } catch {
      setError('No se pudieron cargar los ajustes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setForm(emptyForm);
    setFormErrors({});
    setOpenForm(true);
  };

  const openEdit = (item) => {
    setForm({
      _id: item._id,
      user_id: item.user_id || '',
      type: item.type || 'BONUS',
      amount: item.amount ?? '',
      description: item.description || '',
      effective_date: parseDateInput(item.effective_date),
    });
    setFormErrors({});
    setOpenForm(true);
  };

  const handleSave = async () => {
    const errors = validateForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0 || !storeId) return;

    setSaving(true);
    setFeedback({ type: null, message: '' });
    try {
      const payload = buildPayload(form);
      if (form._id) {
        await PayrollService.updateAdjustment(storeId, form._id, payload);
        setFeedback({ type: 'success', message: 'Ajuste actualizado.' });
      } else {
        await PayrollService.createAdjustment(storeId, payload);
        setFeedback({ type: 'success', message: 'Ajuste creado.' });
      }
      setOpenForm(false);
      setForm(emptyForm);
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'No se pudo guardar el ajuste.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!storeId || !toDelete?._id) return;
    setDeleting(true);
    setFeedback({ type: null, message: '' });
    try {
      await PayrollService.deleteAdjustment(storeId, toDelete._id);
      setFeedback({ type: 'success', message: 'Ajuste eliminado.' });
      setToDelete(null);
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'No se pudo eliminar el ajuste.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Ajustes (Bonos y descuentos)</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Crear ajuste</Button>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
        <TextField
          label="Desde"
          type="date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={filters.from}
          onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
        />
        <TextField
          label="Hasta"
          type="date"
          InputLabelProps={{ shrink: true }}
          size="small"
          value={filters.to}
          onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
        />
        <TextField
          label="Cajero"
          size="small"
          select
          value={filters.user_id}
          onChange={(e) => setFilters((prev) => ({ ...prev, user_id: e.target.value }))}
          sx={{ minWidth: 210 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {asArray(users).map((u) => {
            const userId = u._id || u.id;
            return (
              <MenuItem key={userId} value={userId}>
                {resolveCashierName(userId, usersMap)}
              </MenuItem>
            );
          })}
        </TextField>
        <TextField
          label="Tipo"
          size="small"
          select
          value={filters.type}
          onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="BONUS">BONUS</MenuItem>
          <MenuItem value="DISCOUNT">DISCOUNT</MenuItem>
        </TextField>
      </Stack>

      {feedback.type && (
        <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback({ type: null, message: '' })}>
          {feedback.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadData}>Reintentar</Button>}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Fecha efectiva</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cajero</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Monto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 6 }).map((_, idx) => (
              <TableRow key={`sk-${idx}`}>
                <TableCell><Skeleton width={110} /></TableCell>
                <TableCell><Skeleton width={140} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell align="right"><Skeleton width={70} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}

            {!loading && !error && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No hay ajustes para los filtros seleccionados</Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && filteredRows.map((row) => (
              <TableRow hover key={row._id}>
                <TableCell>{formatDateAR(row.effective_date)}</TableCell>
                <TableCell>{resolveCashierName(row.user_id, usersMap)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.type}
                    color={row.type === 'BONUS' ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatARS(row.amount)}</TableCell>
                <TableCell>{row.description || '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)} title="Editar">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setToDelete(row)} title="Eliminar">
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={openForm} onClose={() => !saving && setOpenForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Editar ajuste' : 'Nuevo ajuste'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Cajero"
              select
              value={form.user_id}
              onChange={(e) => setForm((prev) => ({ ...prev, user_id: e.target.value }))}
              error={!!formErrors.user_id}
              helperText={formErrors.user_id}
              required
            >
              {asArray(users).map((u) => {
                const userId = u._id || u.id;
                return (
                  <MenuItem key={userId} value={userId}>
                    {resolveCashierName(userId, usersMap)}
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              label="Tipo"
              select
              value={form.type}
              onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              error={!!formErrors.type}
              helperText={formErrors.type}
              required
            >
              {TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Monto"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              required
            />
            <TextField
              label="Fecha efectiva"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.effective_date}
              onChange={(e) => setForm((prev) => ({ ...prev, effective_date: e.target.value }))}
              error={!!formErrors.effective_date}
              helperText={formErrors.effective_date}
              required
            />
            <TextField
              label="Descripción"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)}>
        <DialogTitle>Eliminar ajuste</DialogTitle>
        <DialogContent dividers>
          <Typography>
            ¿Seguro que querés eliminar este ajuste de {formatARS(toDelete?.amount)}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PayrollAdjustmentsPage;
